
import { Component, MixedVue, mixins } from "@/vue";
import { debounce } from "debounce";
import { VNode } from "vue";
import { Prop } from "vue-property-decorator";

@Component({
  name: "VirtualScroll",
})
export default class VirtualScroll<T> extends mixins(MixedVue) {
  $scopedSlots: {
    item: (bindings: { item: any }) => VNode[];
  };

  $props: {
    items: T[];
    getItemKey: (item: T) => string | number | symbol;
    getItemHeight: (item: T) => number;
    itemCount: number;
    height?: number;
    renderAhead?: number;
    mode?: "container" | "parent";
  };

  @Prop(Array)
  items: T[];

  @Prop(Function)
  getItemKey: (item: T) => string | number | symbol;

  @Prop(Function)
  getItemHeight: (item: T) => number;

  @Prop(Number)
  itemCount: number;

  @Prop({ type: Number, required: false, default: null })
  height: number;

  @Prop({ type: Number, required: false, default: 20 })
  renderAhead: number;

  @Prop({ type: String, required: false, default: "container" })
  mode: "container" | "parent";

  $el: HTMLElement;

  scrollTop: number = 0;
  scrollContainer: HTMLElement = null;
  computedHeight: number = 0;
  animationFrame: number;

  mounted() {
    if (this.mode == "container") {
      this.scrollContainer = this.$el;
      this.computedHeight = this.height;
    } else if (this.mode == "parent") {
      let parent = this.$el.parentElement;
      while (parent.classList.contains("skip-virtual-scroll-parent")) {
        parent = parent.parentElement;
      }
      this.scrollContainer = parent;
      this.computedHeight = this.scrollContainer.clientHeight;
    }
    this.addScrollListener();
  }

  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  }

  get childPositions() {
    let results = [0];
    for (let i = 1; i < this.itemCount; i++) {
      results.push(results[i - 1] + this.getItemHeight(this.items[i - 1]));
    }
    return results;
  }

  get totalHeight() {
    return this.childPositions[this.itemCount - 1] + this.getItemHeight(this.items[this.itemCount - 1]);
  }

  get firstVisibleNode() {
    return findStartNode(this.scrollTop, this.childPositions, this.itemCount);
  }

  get startNode() {
    return Math.max(0, this.firstVisibleNode - this.renderAhead);
  }

  get lastVisibleNode() {
    return findEndNode(this.childPositions, this.firstVisibleNode, this.itemCount, this.computedHeight);
  }

  get endNode() {
    return Math.min(this.itemCount - 1, this.lastVisibleNode + this.renderAhead);
  }

  get offsetY() {
    return this.childPositions[this.startNode];
  }

  get visibleChildren() {
    return this.items.slice(this.startNode, this.endNode + 1);
  }

  addScrollListener() {
    this.computeScrollTop();
    this.scrollContainer.addEventListener("scroll", debounce(this.onScroll, 100));
  }

  computeScrollTop() {
    let scrollTop = this.scrollContainer.scrollTop;
    if (this.mode == "parent") {
      scrollTop = scrollTop - this.$el.offsetTop;
    }
    this.scrollTop = scrollTop > 0 ? scrollTop : 0;
  }

  onScroll() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.animationFrame = requestAnimationFrame(() => {
      this.computeScrollTop();
    });
  }
}

function findStartNode(scrollTop: number, nodePositions: number[], itemCount: number) {
  let startRange = 0;
  let endRange = itemCount - 1;
  if (itemCount == 0) return;
  while (endRange !== startRange) {
    const middle = Math.floor((endRange - startRange) / 2 + startRange);

    if (nodePositions[middle] <= scrollTop && nodePositions[middle + 1] > scrollTop) {
      return middle;
    }

    if (middle === startRange) {
      return endRange;
    } else {
      if (nodePositions[middle] <= scrollTop) {
        startRange = middle;
      } else {
        endRange = middle;
      }
    }
  }
  return itemCount;
}

function findEndNode(nodePositions: number[], startNode: number, itemCount: number, height: number) {
  let endNode: number;
  for (endNode = startNode; endNode < itemCount; endNode++) {
    if (nodePositions[endNode] > nodePositions[startNode] + height) {
      return endNode;
    }
  }
  return endNode;
}
